import React from 'react';

import { Navigate } from 'react-router-dom';

import { lazyWithRetry } from '@shared/factory';

import {
	ChimeraAccessKey,
	IPFBRoute,
	MerchantType,
	Permission,
	RouteId,
	RoutePath,
} from '@/types';

const Login = lazyWithRetry(
	() => import(/* webpackChunkName: 'Login' */ '@/containers/login/Login.tsx'),
	'Login',
);
const UserVerify = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'UserVerify' */ '@/containers/login/UserVerify.tsx'
		),
	'UserVerify',
);
const ResetPassword = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'ResetPassword' */ '@/containers/ResetPassword.js'
		),
	'ResetPassword',
);
const Dashboard = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Dashboard' */ '@/containers/dashBoard/Dashboard.tsx'
		),
	'Dashboard',
);
const Transactions = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Transactions' */ '@/containers/transactions/Transactions.js'
		),
	'Transactions',
);

const LiveTransactions = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'LiveTransactions' */ '@/containers/transactions/liveTransactions/LiveTransactions.tsx'
		),
	'LiveTransactions',
);
const TransactionHistory = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'TransactionHistory' */ '@/containers/transactions/transactionsHistory/TransactionHistory.tsx'
		),
	'TransactionHistory',
);
const Refunds = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Refunds' */ '@/containers/transactions/refunds/Refunds.tsx'
		),
	'Refunds',
);

const Reports = lazyWithRetry(
	() =>
		import(/* webpackChunkName: 'Reports' */ '@/containers/reports/Reports.js'),
	'Reports',
);

const TransactionReports = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'TransactionReports' */ '@/containers/reports/transactionReports/TransactionReports.js'
		),
	'TransactionReports',
);
const TransactionDailyReports = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'TransactionDailyReports' */ '@/containers/reports/transactionDailyReports/TransactionDailyReports.view.tsx'
		),
	'TransactionDailyReports',
);
const RefundReports = lazyWithRetry(
	() => import('@/containers/reports/refundReports/RefundReports.tsx'),
);
const RefundDailyReports = lazyWithRetry(
	() =>
		import(
			'@/containers/reports/refundDailyReports/RefundDailyReports.view.tsx'
		),
);
const QMCReports = lazyWithRetry(
	() => import('@/containers/reports/qmcReports/QMCReports.view.tsx'),
);
const PayLinkOld = lazyWithRetry(
	() =>
		import(/* webpackChunkName: 'PaylinksOld' */ '@/containers/PayLinkOld.js'),
	'PayLinkOld',
);
const PayLinkNew = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Paylinks' */ '@/containers/payLinkNew/Paylink.tsx'
		),
	'PayLinkNew',
);
const TransactionDetails = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'TransactionDetails' */ '@/containers/transactionDetails/TransactionDetails.js'
		),
	'TransactionDetails',
);
const ManageUsers = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'ManageUsers' */ '@/containers/ManageUsers.tsx'
		),
	'ManageUsers',
);

// ----- Old Routes -----
const Settlements = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'SettlementsView' */ '@/containers/settlements/SettlementsView.tsx'
		),
	'SettlementsViewV2',
);
const Qr = lazyWithRetry(
	() => import(/* webpackChunkName: 'QR' */ '@/views/container/QR/index.js'),
	'EnabledQR',
);
const EnabledQR = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'QR' */ '@/views/container/QR/EnabledQR/index.js'
		),
	'EnabledQR',
);
const QRDisabled = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'QR' */ '@/views/container/QR/DisabledQR/index.js'
		),
	'QRDisabled',
);
const Profile = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Profile' */ '@/containers/profile/Profile.tsx'
		),
	'Profile',
);

const Stores = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Stores' */ '@/containers/stores/Stores.views.tsx'
		),
	'Stores',
);

const StoresDetails = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Stores' */ '@/views/container/Stores/StoreDetail/index.js'
		),
	'StoresDetails',
);
const Faq = lazyWithRetry(
	() => import(/* webpackChunkName: 'Faq' */ '@/containers/faq/FAQ.view.tsx'),
	'Faq',
);
const DeveloperSettings = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'DeveloperSettings' */ '@/containers/developerSettings/DeveloperSettings.tsx'
		),
	'DeveloperSettings',
);
const PageNotFound = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'PageNotFound' */ '@/views/container/PageNotFound/index.js'
		),
	'PageNotFound',
);

const PgActivation = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'PgActivation' */ '@/containers/pgActivation/PgActivation.tsx'
		),
	'PgActivation',
);

const AppLink = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'AppLink' */ '@/containers/appLink/AppLink.tsx'
		),
	'AppLink',
);

const OnlineKyc = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'OnlineKyc' */ '@/containers/onlineKyc/OnlineKyc.tsx'
		),
	'OnlineKyc',
);

const MORegister = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'MO_Register' */ '@/online-merchant-onboarding/pages/Register/Register.tsx'
		),
	'MO_Register',
);

const PGRegister = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'PaymentGatewayRegister' */ '@/components/PaymentGatewayRegister/PaymentGatewayRegister.tsx'
		),
	'PG_Register',
);

const ApiKeys = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'ApiKeys' */ '@/containers/developerSettings/apiKeys/ApiKeys.tsx'
		),
);

const Webhooks = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'Webhooks' */ '@/containers/developerSettings/webhooks/Webhooks.tsx'
		),
);

const LoginRedirection = lazyWithRetry(
	() =>
		import(
			/* webpackChunkName: 'LoginRedirection' */ '@/containers/loginRedirection/LoginRedirection.tsx'
		),
	'LoginRedirection',
);

const withoutLayout = {
	withContentPadding: false,
	showSideNavbar: false,
	showTopNavbar: false,
	showFooter: false,
};

export const pfbRoutes: IPFBRoute[] = [
	{
		id: RouteId.DASHBOARD_PAGE,
		path: RoutePath.DASHBOARD,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {},
		element: <Dashboard />,
	},
	{
		id: RouteId.HOME,
		path: RoutePath.HOME,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {},
		element: <Navigate to={RoutePath.DASHBOARD} replace />,
	},
	{
		id: RouteId.TRANSACTION_DETAILS_PAGE,
		path: RoutePath.TRANSACTION_DETAILS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {},
		element: <TransactionDetails />,
	},
	{
		id: RouteId.TRANSACTIONS_PAGE,
		path: RoutePath.TRANSACTIONS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {
			online: [Permission.TRANSACTION_ACCESS],
			offline: [Permission.TRANSACTION_ACCESS],
		},
		element: <Transactions />,
		children: [
			{
				id: RouteId.LIVE_TRANSACTIONS_PAGE,
				path: RoutePath.LIVE_TRANSACTIONS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.TRANSACTION_ACCESS],
					offline: [Permission.TRANSACTION_ACCESS],
					isSuperMerchant: false,
				},
				element: <LiveTransactions />,
			},
			{
				id: RouteId.TRANSACTIONS_HISTORY_PAGE,
				path: RoutePath.TRANSACTIONS_HISTORY,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.TRANSACTION_ACCESS],
					offline: [Permission.TRANSACTION_ACCESS],
				},
				element: <TransactionHistory />,
			},
			{
				id: RouteId.REFUNDS_PAGE,
				path: RoutePath.REFUNDS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [
						Permission.BATCH_REFUND_ACCESS,
						Permission.REFUND_LIST_ACCESS,
						Permission.REFUND_REPORTS_ACCESS,
					],
					offline: [
						Permission.BATCH_REFUND_ACCESS,
						Permission.REFUND_LIST_ACCESS,
						Permission.REFUND_REPORTS_ACCESS,
					],
				},
				element: <Refunds />,
			},
		],
	},
	{
		id: RouteId.REPORTS_PAGE,
		path: RoutePath.REPORTS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {
			online: [
				Permission.REPORTS_ACCESS,
				Permission.TRANSACTION_REPORTS_ACCESS,
			],
			offline: [
				Permission.REPORTS_ACCESS,
				Permission.TRANSACTION_REPORTS_ACCESS,
			],
		},
		element: <Reports />,
		children: [
			{
				id: RouteId.TRANSACTION_REPORTS_PAGE,
				path: RoutePath.TRANSACTION_REPORTS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.REPORTS_ACCESS],
					offline: [Permission.REPORTS_ACCESS],
				},
				element: <TransactionReports />,
			},
			{
				id: RouteId.DAILY_TRANSACTION_REPORTS_PAGE,
				path: RoutePath.DAILY_TRANSACTION_REPORTS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.TRANSACTION_REPORTS_ACCESS],
					offline: [Permission.TRANSACTION_REPORTS_ACCESS],
				},
				element: <TransactionDailyReports />,
			},
			{
				id: RouteId.REFUND_REPORTS_PAGE,
				path: RoutePath.REFUND_REPORTS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.REFUND_REPORTS_ACCESS],
					offline: [Permission.REFUND_REPORTS_ACCESS],
				},
				element: <RefundReports />,
			},
			{
				id: RouteId.DAILY_REFUND_REPORTS_PAGE,
				path: RoutePath.DAILY_REFUND_REPORTS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [
						Permission.REFUND_REPORTS_ACCESS,
						Permission.BATCH_REFUND_ACCESS,
						Permission.REFUND_LIST_ACCESS,
						Permission.TRANSACTION_REPORTS_ACCESS,
					],
					offline: [
						Permission.REFUND_REPORTS_ACCESS,
						Permission.BATCH_REFUND_ACCESS,
						Permission.REFUND_LIST_ACCESS,
						Permission.TRANSACTION_REPORTS_ACCESS,
					],
				},
				element: <RefundDailyReports />,
			},
			{
				id: RouteId.QMC_REPORTS_PAGE,
				path: RoutePath.QMC_REPORTS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
				permissions: {
					online: [Permission.QMC_REPORT_ACCESS],
					offline: [Permission.QMC_REPORT_ACCESS],
				},
				element: <QMCReports />,
			},
		],
	},
	{
		id: RouteId.SETTLEMENTS_PAGE,
		path: RoutePath.SETTLEMENTS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {
			online: [Permission.SETTLEMENTS_REPORTS_ACCESS],
			offline: [Permission.SETTLEMENTS_REPORTS_ACCESS],
		},
		element: <Settlements />,
	},
	{
		id: RouteId.ONLINE_PAYLINKS_PAGE,
		path: RoutePath.ONLINE_PAYLINKS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.ONLINE],
		permissions: {
			online: [Permission.PAYLINK_READ_ACCESS],
			chimera: { [ChimeraAccessKey.ONLINE_PAYLINKS_HIDE]: false },
		},
		element: <PayLinkNew />,
	},
	{
		id: RouteId.OFFLINE_PAYLINKS_PAGE,
		path: RoutePath.OFFLINE_PAYLINKS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE],
		permissions: {
			offline: [Permission.paylink_read_access],
			chimera: { [ChimeraAccessKey.PAYLINK_ENABLED]: true },
			isUPIVerified: true,
		},
		element: <PayLinkOld />,
	},
	{
		id: RouteId.QR_PAGE,
		path: RoutePath.QR,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE],
		permissions: {
			offline: [Permission.QR_CODES_ACCESS],
		},
		element: <Qr />,
		children: [
			{
				id: RouteId.ENABLED_QRS_PAGE,
				path: RoutePath.ENABLED_QRS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE],
				permissions: {
					offline: [Permission.QR_CODES_ACCESS],
				},
				element: <EnabledQR />,
			},
			{
				id: RouteId.DISABLED_QRS_PAGE,
				path: RoutePath.DISABLED_QRS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.OFFLINE],
				permissions: {
					offline: [Permission.QR_CODES_ACCESS],
				},
				element: <QRDisabled />,
			},
		],
	},
	{
		id: RouteId.USER_PROFILE_PAGE,
		path: RoutePath.USER_PROFILE,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {
			online: [Permission.PROFILE_ACCESS],
			offline: [Permission.PROFILE_ACCESS],
		},
		element: <Profile />,
	},
	{
		id: RouteId.MANAGE_USERS_PAGE,
		path: RoutePath.MANAGE_USERS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {
			online: [Permission.USERS_ACCESS],
			offline: [Permission.USERS_ACCESS],
		},
		element: <ManageUsers />,
	},
	{
		id: RouteId.STORES_PAGE,
		path: RoutePath.STORES,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE],
		permissions: {
			offline: [Permission.STORE_ACCESS],
		},
		element: <Stores />,
	},
	{
		id: RouteId.STORES_DETAILS_PAGE,
		path: RoutePath.STORE_DETAILS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE],
		permissions: {
			offline: [Permission.STORE_ACCESS],
		},
		element: <StoresDetails />,
	},
	{
		id: RouteId.DEVELOPER_SETTINGS_PAGE,
		path: RoutePath.DEVELOPER_SETTINGS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.ONLINE],
		permissions: {},
		element: <DeveloperSettings />,
		children: [
			{
				id: RouteId.API_KEYS,
				path: RoutePath.API_KEYS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.ONLINE],
				permissions: {},
				element: <ApiKeys />,
			},
			{
				id: RouteId.WEBHOOKS,
				path: RoutePath.WEBHOOKS,
				isAuthenticated: true,
				allowedMerchants: [MerchantType.ONLINE],
				permissions: {},
				element: <Webhooks />,
			},
		],
	},
	{
		id: RouteId.PG_ACTIVATION_PAGE,
		path: RoutePath.PG_ACTIVATION,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.ONLINE],
		permissions: {
			online: [Permission.ONLINE_MERCHANT_ONBOARDING],
			isB2bPgActivated: false,
		},
		element: <PgActivation />,
		routeData: { layoutInfo: withoutLayout },
	},
	{
		id: RouteId.COMPLETE_KYC_PAGE,
		path: RoutePath.COMPLETE_KYC,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.ONLINE],
		permissions: {
			online: [Permission.ONLINE_MERCHANT_ONBOARDING],
		},
		element: <OnlineKyc />,
		routeData: { layoutInfo: withoutLayout },
	},
	{
		id: RouteId.LOGIN_PAGE,
		path: RoutePath.LOGIN,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <Login />,
	},
	{
		id: RouteId.NOT_FOUND_PAGE,
		path: RoutePath.NOT_FOUND,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <PageNotFound />,
	},
	{
		id: RouteId.RESET_PASSWORD_PAGE,
		path: RoutePath.RESET_PASSWORD,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <ResetPassword />,
	},
	{
		id: RouteId.USER_VERIFY_PAGE,
		path: RoutePath.USER_VERIFY,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <UserVerify />,
	},
	{
		id: RouteId.APPLINK,
		path: RoutePath.APPLINK,
		isAuthenticated: false,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {},
		element: <AppLink />,
	},
	{
		id: RouteId.FAQS_PAGE,
		path: RoutePath.FAQS,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.OFFLINE, MerchantType.ONLINE],
		permissions: {},
		element: <Faq />,
	},
	{
		id: RouteId.MO_REGISTER_PAGE,
		path: RoutePath.REGISTER,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <MORegister />,
	},
	{
		id: RouteId.PG_REGISTER_PAGE,
		path: RoutePath.PG_REGISTER,
		isAuthenticated: false,
		allowedMerchants: [],
		permissions: {},
		element: <PGRegister />,
	},
	{
		id: RouteId.LOGIN_REDIRECTION_PAGE,
		path: RoutePath.LOGIN_REDIRECTION,
		isAuthenticated: true,
		allowedMerchants: [MerchantType.ONLINE, MerchantType.OFFLINE],
		permissions: {},
		element: <LoginRedirection />,
		routeData: { layoutInfo: withoutLayout },
	},
];

// No need to edit this part ---
const getFlattenedRoutes = (routes: IPFBRoute[]) =>
	routes.reduce(
		(accumulatedRoutes: Record<RouteId, IPFBRoute>, currentRoute) => {
			const { children, ...rest } = currentRoute;
			accumulatedRoutes[rest.id] = rest;

			if (children) {
				Object.assign(accumulatedRoutes, getFlattenedRoutes(children));
			}

			return accumulatedRoutes;
		},
		{} as Record<RouteId, IPFBRoute>,
	);

export const flattenedPfbRoutes = getFlattenedRoutes(pfbRoutes);
